import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listActionCategories } from "../../api/actions.api";
import { useAuth } from "../../useAuth";
import { canAccessAdminModules } from "../../accessControl";
import {
  createTaxonomyValue,
  listTaxonomyValues,
  updateTaxonomyValue,
} from "../../api/taxonomy.api";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import { spacing } from "../../ui/tokens";

const CATEGORY_KEY = "action_category";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const FIXED_STATUSES = [
  {
    value: "OPEN",
    label: "Open",
    description: "Default workflow state for newly created actions.",
  },
  {
    value: "IN_PROGRESS",
    label: "In Progress",
    description: "Indicates work has started.",
  },
  {
    value: "BLOCKED",
    label: "Blocked",
    description: "Indicates the action cannot currently progress.",
  },
  {
    value: "COMPLETED",
    label: "Completed",
    description: "Final workflow state enforced by backend completion checks.",
  },
];

const FIXED_PRIORITIES = [
  {
    value: "LOW",
    label: "Low",
    description: "Lowest escalation level currently supported by the API.",
  },
  {
    value: "MEDIUM",
    label: "Medium",
    description: "Standard operating priority.",
  },
  {
    value: "HIGH",
    label: "High",
    description: "Elevated response priority.",
  },
  {
    value: "CRITICAL",
    label: "Critical",
    description: "Highest supported urgency level.",
  },
];

const SETTINGS_COVERAGE = [
  {
    key: "categories",
    title: "Categories",
    status: "Configurable",
    description: "Managed through the shared taxonomy service and already consumed by Actions category selectors.",
  },
  {
    key: "statuses",
    title: "Statuses",
    status: "Fixed",
    description: "Defined by backend enums and workflow checks. No settings endpoint exists today.",
  },
  {
    key: "priorities",
    title: "Priority Levels",
    status: "Fixed",
    description: "Defined by backend enums. No settings endpoint exists today.",
  },
  {
    key: "types",
    title: "Action Types",
    status: "Gap",
    description: "No Actions type entity, taxonomy category, or settings endpoint was found in this repo.",
  },
  {
    key: "templates",
    title: "Templates",
    status: "Gap",
    description: "No Actions template configuration model or endpoint was found in this repo.",
  },
  {
    key: "rules",
    title: "Defaults And Rules",
    status: "Gap",
    description: "No Actions defaults, routing rules, or module settings endpoint was found in this repo.",
  },
];

const toSlug = (value = "") =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeValue = (value = "") => String(value || "").trim().toLowerCase();

function getCategoryValidationError({ categories, label, value, editingId }) {
  const trimmedLabel = String(label || "").trim();
  if (!trimmedLabel) return "Category label is required.";

  const normalizedLabel = normalizeValue(trimmedLabel);
  const duplicateLabel = (categories || []).some((item) => {
    if (!item) return false;
    if (editingId && String(item.id) === String(editingId)) return false;
    return normalizeValue(item.label) === normalizedLabel;
  });
  if (duplicateLabel) return "Category label must be unique.";

  const trimmedValue = String(value || "").trim() || toSlug(trimmedLabel);
  if (!trimmedValue) return "Category value is required.";

  const normalizedCategoryValue = normalizeValue(trimmedValue);
  const duplicateValue = (categories || []).some((item) => {
    if (!item) return false;
    if (editingId && String(item.id) === String(editingId)) return false;
    return normalizeValue(item.value) === normalizedCategoryValue;
  });
  if (duplicateValue) return "Category value must be unique.";

  return null;
}

function SummaryCard({ label, value, helpText }) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={summaryValueStyle}>{value}</div>
      {helpText ? <div style={summaryHelpStyle}>{helpText}</div> : null}
    </div>
  );
}

function ControlledValueList({ title, description, values }) {
  return (
    <div style={cardStyle}>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <p style={sectionDescriptionStyle}>{description}</p>
      <div style={{ display: "grid", gap: spacing.sm }}>
        {values.map((entry) => (
          <div key={entry.value} style={fixedValueRowStyle}>
            <div>
              <div style={{ fontWeight: 600 }}>{entry.label}</div>
              <div style={{ color: "#6b7280", fontSize: 13 }}>{entry.description}</div>
            </div>
            <code style={valueCodeStyle}>{entry.value}</code>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoverageCard({ title, status, description }) {
  return (
    <div style={coverageCardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: spacing.sm, alignItems: "center" }}>
        <div style={{ fontWeight: 600 }}>{title}</div>
        <span
          style={
            status === "Configurable"
              ? configurableTagStyle
              : status === "Fixed"
                ? fixedTagStyle
                : gapTagStyle
          }
        >
          {status}
        </span>
      </div>
      <div style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.5 }}>{description}</div>
    </div>
  );
}

export default function ActionsAdmin() {
  const { user } = useAuth();
  const canManageAdminModules = canAccessAdminModules(user);
  const [categories, setCategories] = useState([]);
  const [detectedCategories, setDetectedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState({ label: "", value: "", status: "active" });
  const [editingId, setEditingId] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, category: null, nextStatus: null });
  const editorCardRef = useRef(null);
  const labelInputRef = useRef(null);

  const activeCount = useMemo(
    () => categories.filter((item) => String(item.status || "active").toLowerCase() === "active").length,
    [categories]
  );

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [taxonomyResult, actionCategoryResult] = await Promise.allSettled([
        listTaxonomyValues(CATEGORY_KEY),
        listActionCategories(),
      ]);

      if (taxonomyResult.status === "rejected") {
        throw taxonomyResult.reason;
      }

      const taxonomyValues = taxonomyResult.value?.data || [];
      const actionValues =
        actionCategoryResult.status === "fulfilled"
          ? actionCategoryResult.value?.data || []
          : [];
      const configuredKeys = new Set(
        (taxonomyValues || []).flatMap((item) =>
          [item?.label, item?.value].map((value) => normalizeValue(value)).filter(Boolean)
        )
      );
      const uncataloguedValues = Array.from(
        new Set(
          (Array.isArray(actionValues) ? actionValues : [])
            .map((value) => String(value || "").trim())
            .filter(Boolean)
            .filter((value) => !configuredKeys.has(normalizeValue(value)))
        )
      );

      setCategories(taxonomyValues);
      setDetectedCategories(uncataloguedValues);
    } catch (err) {
      setCategories([]);
      setDetectedCategories([]);
      setError(err?.response?.data?.detail || err?.message || "Unable to load Actions settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageAdminModules) return;
    loadCategories();
  }, [canManageAdminModules, loadCategories]);

  useEffect(() => {
    if (!editingId) return;
    editorCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    labelInputRef.current?.focus?.();
    labelInputRef.current?.select?.();
  }, [editingId]);

  const resetForm = () => {
    setFormState({ label: "", value: "", status: "active" });
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const value = formState.value?.trim() || toSlug(formState.label);
    const validationError = getCategoryValidationError({
      categories,
      label: formState.label,
      value,
      editingId,
    });
    if (validationError) {
      setActionMessage({ tone: "error", text: validationError });
      return;
    }

    setSaving(true);
    setActionMessage(null);
    try {
      if (editingId) {
        await updateTaxonomyValue(CATEGORY_KEY, editingId, {
          label: formState.label.trim(),
          status: formState.status,
        });
        setActionMessage({ tone: "success", text: "Category updated." });
      } else {
        await createTaxonomyValue(CATEGORY_KEY, {
          label: formState.label.trim(),
          value,
          status: formState.status,
        });
        setActionMessage({ tone: "success", text: "Category created." });
      }
      resetForm();
      loadCategories();
    } catch (err) {
      setActionMessage({
        tone: "error",
        text: err?.response?.data?.detail || err?.message || "Unable to save category.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormState({
      label: category.label || "",
      value: category.value || "",
      status: category.status || "active",
    });
    setActionMessage(null);
  };

  const handleUseDetectedCategory = (label) => {
    setEditingId(null);
    setFormState({
      label,
      value: toSlug(label),
      status: "active",
    });
    setActionMessage(null);
    editorCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    labelInputRef.current?.focus?.();
    labelInputRef.current?.select?.();
  };

  const toggleStatus = async (category) => {
    if (!category?.id) return;
    const nextStatus =
      confirmState.nextStatus ||
      (String(category.status || "active").toLowerCase() === "active" ? "inactive" : "active");
    setSaving(true);
    setActionMessage(null);
    try {
      await updateTaxonomyValue(CATEGORY_KEY, category.id, { status: nextStatus });
      setActionMessage({
        tone: "success",
        text: nextStatus === "active" ? "Category activated." : "Category deactivated.",
      });
      loadCategories();
    } catch (err) {
      setActionMessage({
        tone: "error",
        text: err?.response?.data?.detail || err?.message || "Unable to update category status.",
      });
    } finally {
      setSaving(false);
      setConfirmState({ open: false, category: null, nextStatus: null });
    }
  };

  if (!canManageAdminModules) {
    return (
      <div style={guardStyle}>
        <h2 style={{ marginTop: 0 }}>Actions Module Settings</h2>
        <p style={{ marginBottom: 0 }}>
          You need Manage access to Admin Module Portal to manage Actions configuration.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: spacing.xl }}>
      <div>
        <h2 style={{ marginBottom: spacing.xs }}>Actions Module Settings</h2>
        <p style={pageIntroStyle}>
          Configure Actions module metadata using the same admin settings patterns as the rest of the platform.
          Categories are the only Actions values with existing backend configuration support in this repo.
        </p>
      </div>

      <div style={summaryGridStyle}>
        <SummaryCard
          label="Active categories"
          value={activeCount}
          helpText="Available for future Actions metadata consumers."
        />
        <SummaryCard
          label="Fixed statuses"
          value={FIXED_STATUSES.length}
          helpText="Read-only workflow values enforced by the API."
        />
        <SummaryCard
          label="Fixed priorities"
          value={FIXED_PRIORITIES.length}
          helpText="Read-only urgency values enforced by the API."
        />
      </div>

      <div style={infoCardStyle}>
        <div style={{ fontWeight: 600 }}>Current backend support</div>
        <div style={sectionDescriptionStyle}>
          This page intentionally manages module metadata, not action records. It reuses the existing taxonomy service
          for categories and surfaces the remaining Actions settings as fixed or unsupported based on the codebase as it
          exists today.
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Configuration coverage</h3>
        <p style={sectionDescriptionStyle}>
          Supported settings are editable below. Fixed and gap items are shown here so admins can see what is and is not
          configurable today.
        </p>
        <div style={coverageGridStyle}>
          {SETTINGS_COVERAGE.map((item) => (
            <CoverageCard
              key={item.key}
              title={item.title}
              status={item.status}
              description={item.description}
            />
          ))}
        </div>
      </div>

      <div ref={editorCardRef} style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Action Categories</h3>
        <p style={sectionDescriptionStyle}>
          Manage module-level Action categories using the existing taxonomy infrastructure and admin permissions.
        </p>
        <h4 style={subsectionHeadingStyle}>{editingId ? "Edit category" : "Add category"}</h4>
        <form onSubmit={handleSubmit} style={{ display: "grid", gap: spacing.md }}>
          <Input
            ref={labelInputRef}
            label="Label"
            value={formState.label}
            onChange={(e) => setFormState((prev) => ({ ...prev, label: e.target.value }))}
            placeholder="e.g., Remediation"
          />
          <Input
            label="Value"
            value={formState.value}
            onChange={(e) => setFormState((prev) => ({ ...prev, value: e.target.value }))}
            placeholder="Optional machine key (auto-generated if blank)"
            disabled={Boolean(editingId)}
          />
          <label style={fieldLabel}>
            <span style={labelText}>Status</span>
            <Select
              value={formState.status}
              onChange={(e) => setFormState((prev) => ({ ...prev, status: e.target.value }))}
              style={inputStyle}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </label>

          {actionMessage && (
            <div style={actionMessage.tone === "error" ? errorBannerStyle : successBannerStyle}>
              {actionMessage.text}
            </div>
          )}

          <div style={{ display: "flex", gap: spacing.sm }}>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save changes" : "Create category"}
            </Button>
            {editingId ? (
              <Button type="button" variant="secondary" onClick={resetForm} disabled={saving}>
                Cancel
              </Button>
            ) : null}
          </div>
        </form>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Configured categories</h3>
          {loading ? <span style={{ color: "#6b7280" }}>Loading...</span> : null}
        </div>
        <p style={sectionDescriptionStyle}>
          Active categories can be used by Actions forms. Inactive categories remain visible for existing data but are
          not offered in active taxonomy-backed selectors.
        </p>

        {error ? <div style={errorBannerStyle}>{error}</div> : null}

        {!loading && !categories.length ? (
          <div style={{ padding: spacing.md, color: "#6b7280" }}>
            No action categories configured yet.
          </div>
        ) : null}

        {categories.length > 0 ? (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Label</th>
                <th style={thStyle}>Value</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => {
                const statusValue = String(category.status || "active").toLowerCase();
                return (
                  <tr key={category.id}>
                    <td style={tdStyle}>{category.label}</td>
                    <td style={tdStyle}>
                      <code style={valueCodeStyle}>{category.value}</code>
                    </td>
                    <td style={tdStyle}>
                      <span style={statusValue === "inactive" ? inactiveTagStyle : activeTagStyle}>
                        {statusValue}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: spacing.sm }}>
                        <Button
                          variant="secondary"
                          onClick={() => handleEdit(category)}
                          disabled={saving}
                          aria-label={`Edit category ${category.label}`}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            setConfirmState({
                              open: true,
                              category,
                              nextStatus: statusValue === "inactive" ? "active" : "inactive",
                            })
                          }
                          disabled={saving}
                          aria-label={`${
                            statusValue === "inactive" ? "Activate" : "Deactivate"
                          } category ${category.label}`}
                        >
                          {statusValue === "inactive" ? "Activate" : "Deactivate"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </div>

      {detectedCategories.length > 0 ? (
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>Detected on existing actions</h3>
            <span style={{ color: "#6b7280" }}>{detectedCategories.length} found</span>
          </div>
          <p style={sectionDescriptionStyle}>
            These category labels are already present on action records but do not yet exist in Actions admin
            taxonomy. They remain visible in the Actions module because actions store category text directly.
          </p>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Label</th>
                <th style={thStyle}>Suggested value</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {detectedCategories.map((category) => (
                <tr key={category}>
                  <td style={tdStyle}>{category}</td>
                  <td style={tdStyle}>
                    <code style={valueCodeStyle}>{toSlug(category)}</code>
                  </td>
                  <td style={tdStyle}>
                    <Button
                      variant="secondary"
                      onClick={() => handleUseDetectedCategory(category)}
                      disabled={saving}
                    >
                      Use in form
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <ControlledValueList
        title="Fixed workflow statuses"
        description="These values are currently defined by backend enums and workflow rules. They are displayed here for admin visibility but are not configurable through existing settings APIs."
        values={FIXED_STATUSES}
      />

      <ControlledValueList
        title="Fixed priority levels"
        description="These urgency levels are currently defined by backend enums and generated API models. They are not configurable through existing settings APIs."
        values={FIXED_PRIORITIES}
      />

      <ConfirmDialog
        open={confirmState.open}
        title={
          confirmState.nextStatus === "inactive"
            ? "Deactivate category?"
            : "Activate category?"
        }
        message={
          confirmState.nextStatus === "inactive"
            ? "Inactive categories remain visible on existing actions but should no longer appear in active category selectors."
            : "Activate this category so it can be used again in Actions category selectors."
        }
        confirmLabel={confirmState.nextStatus === "inactive" ? "Deactivate" : "Activate"}
        confirmVariant={confirmState.nextStatus === "inactive" ? "destructive" : "primary"}
        onCancel={() => setConfirmState({ open: false, category: null, nextStatus: null })}
        onConfirm={() => toggleStatus(confirmState.category)}
      />
    </div>
  );
}

const pageIntroStyle = {
  margin: 0,
  color: "#6b7280",
  fontSize: 13,
  lineHeight: 1.5,
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.md,
};

const summaryCardStyle = {
  background: "#f8fafc",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: spacing.lg,
  display: "grid",
  gap: spacing.xs,
};

const summaryLabelStyle = {
  fontSize: 12,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const summaryValueStyle = {
  fontSize: 28,
  fontWeight: 700,
};

const summaryHelpStyle = {
  fontSize: 12,
  color: "#6b7280",
};

const infoCardStyle = {
  background: "#f8fafc",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: spacing.lg,
  display: "grid",
  gap: spacing.xs,
};

const coverageGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: spacing.md,
};

const coverageCardStyle = {
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
  padding: spacing.md,
  display: "grid",
  gap: spacing.sm,
};

const cardStyle = {
  background: "white",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: spacing.lg,
  boxShadow: "0 8px 16px rgba(15,23,42,0.04)",
};

const sectionDescriptionStyle = {
  margin: `0 0 ${spacing.md}px`,
  color: "#6b7280",
  fontSize: 13,
  lineHeight: 1.5,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: spacing.md,
};

const thStyle = {
  textAlign: "left",
  fontSize: 12,
  color: "#6b7280",
  paddingBottom: spacing.sm,
  borderBottom: "1px solid #e5e7eb",
};

const tdStyle = {
  padding: `${spacing.sm}px 0`,
  borderBottom: "1px solid #f1f5f9",
  fontSize: 13,
};

const fieldLabel = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
};

const labelText = {
  fontSize: 12,
  color: "#6b7280",
};

const inputStyle = {
  width: "100%",
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderRadius: 6,
  border: "1px solid #d1d5db",
};

const successBannerStyle = {
  padding: spacing.sm,
  borderRadius: 8,
  background: "#dcfce7",
  color: "#166534",
  fontSize: 13,
};

const errorBannerStyle = {
  padding: spacing.sm,
  borderRadius: 8,
  background: "#fee2e2",
  color: "#b91c1c",
  fontSize: 13,
};

const subsectionHeadingStyle = {
  margin: `0 0 ${spacing.md}px`,
  fontSize: 15,
  color: "#111827",
};

const activeTagStyle = {
  display: "inline-flex",
  padding: "2px 10px",
  borderRadius: 999,
  background: "#dcfce7",
  color: "#166534",
  fontSize: 12,
  fontWeight: 600,
};

const inactiveTagStyle = {
  display: "inline-flex",
  padding: "2px 10px",
  borderRadius: 999,
  background: "#e2e8f0",
  color: "#475569",
  fontSize: 12,
  fontWeight: 600,
};

const configurableTagStyle = {
  display: "inline-flex",
  padding: "2px 10px",
  borderRadius: 999,
  background: "#dcfce7",
  color: "#166534",
  fontSize: 12,
  fontWeight: 600,
};

const fixedTagStyle = {
  display: "inline-flex",
  padding: "2px 10px",
  borderRadius: 999,
  background: "#dbeafe",
  color: "#1d4ed8",
  fontSize: 12,
  fontWeight: 600,
};

const gapTagStyle = {
  display: "inline-flex",
  padding: "2px 10px",
  borderRadius: 999,
  background: "#fef3c7",
  color: "#92400e",
  fontSize: 12,
  fontWeight: 600,
};

const valueCodeStyle = {
  fontSize: 12,
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
  borderRadius: 6,
  padding: "2px 6px",
};

const fixedValueRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: spacing.md,
  padding: spacing.md,
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
};

const guardStyle = {
  padding: spacing.lg,
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
};
